/**
 * Tonmessgeräte (S18). Sascha: „Lautheit (LUFS), Pegel, Spektrum —
 * Sendelautheit.“
 *
 * Gemessen wird am **fertigen Film**, nicht an der Vorschau: „sendefähig“ ist
 * eine Aussage über die Datei, die hinausgeht. Die Messung selbst macht
 * ffmpeg (`ebur128`, nach ITU-R BS.1770); hier steht, wie man sie liest und
 * was sie bedeutet.
 *
 * Zwei Zahlen entscheiden:
 *
 *  * **Lautheit (LUFS)** -- wie laut der Film *empfunden* wird, über seine
 *    ganze Länge. Der Rundfunk in Europa liefert auf −23 LUFS (EBU R128),
 *    die grossen Netzwerke auf −14 (YouTube, Spotify).
 *  * **Spitze (dBTP)** -- die höchste Stelle, zwischen den Abtastwerten
 *    gerechnet. Über −1 dBTP knackt es, sobald jemand die Datei umrechnet.
 */
/**
 * Die Zusammenfassung von `ebur128` lesen.
 *
 * ffmpeg schreibt sie ans Ende des Protokolls, eingerückt und mit Namen davor.
 * Genommen wird das **letzte** Vorkommen: bei einer Datei mit mehreren
 * Durchgängen steht die Zusammenfassung zuletzt, und die Zwischenwerte davor
 * sind keine Aussage über das Ganze.
 */
export function leseEbur128(text) {
    const letzte = (muster) => {
        let wert = null;
        for (const treffer of text.matchAll(muster)) {
            const zahl = Number(treffer[1]);
            if (Number.isFinite(zahl))
                wert = zahl;
        }
        return wert;
    };
    const lufs = letzte(/^\s*I:\s*(-?\d+(?:\.\d+)?)\s*LUFS/gm);
    const lra = letzte(/^\s*LRA:\s*(-?\d+(?:\.\d+)?)\s*LU/gm);
    const spitze = letzte(/^\s*Peak:\s*(-?\d+(?:\.\d+)?)\s*dBFS/gm);
    if (lufs === null || lra === null || spitze === null)
        return null;
    return { lufs, lra, spitze };
}
export const ZIELE = [
    { name: 'Rundfunk (EBU R128)', lufs: -23, luft: 1, spitze: -1 },
    { name: 'Netz (YouTube, Instagram)', lufs: -14, luft: 1.5, spitze: -1 },
];
const lu = (x) => `${x > 0 ? '+' : ''}${x.toFixed(1).replace('.', ',')} LU`;
/**
 * Was die Messung für ein Ziel bedeutet.
 *
 * Gesagt wird beides: wie weit daneben *und* in welche Richtung. „Zu leise“
 * ist eine andere Arbeit als „zu laut“, und eine Ampel ohne Richtung schickt
 * einen zum Raten.
 */
export function urteil(gemessen, ziel) {
    const abweichung = Number((gemessen.lufs - ziel.lufs).toFixed(1));
    const trifft = Math.abs(abweichung) <= ziel.luft;
    const spitzeZuHoch = gemessen.spitze > ziel.spitze;
    const teile = [];
    if (trifft)
        teile.push(`trifft ${ziel.name}`);
    else
        teile.push(`${Math.abs(abweichung).toFixed(1).replace('.', ',')} LU zu ${abweichung > 0 ? 'laut' : 'leise'} für ${ziel.name}`);
    if (spitzeZuHoch)
        teile.push(`Spitze ${gemessen.spitze.toFixed(1).replace('.', ',')} dBTP über ${ziel.spitze}`);
    return { passt: trifft && !spitzeZuHoch, abweichung, spitzeZuHoch, satz: `${teile.join(', ')} (${lu(abweichung)})` };
}
/**
 * Den Film auf eine Ziellautheit bringen (S20).
 *
 * `ebur128` (S18) sagt, wie laut der Export geworden ist. Das ist die halbe
 * Auskunft: wer auf −23 liefern muss, will nicht lesen, dass er 4 LU daneben
 * liegt, sondern die Zahl treffen. Also ein Knopf statt einer Regel --
 * `loudnorm` rechnet beim Ausspielen dorthin.
 *
 * **Ein Durchgang, nicht zwei.** `loudnorm` kann genauer sein, wenn man ihm
 * die vorher gemessenen Werte mitgibt; die stünden aber erst fest, wenn der
 * Film schon gerechnet ist, und der Bauplan wird vorher geschrieben. Gemessen
 * an gebautem Material mit wechselndem Pegel trifft der eine Durchgang auf
 * 0,5 LU genau -- innerhalb dessen, was der Rundfunk als getroffen zählt
 * (±1 LU). Nachgemessen wird trotzdem: die Zahl im Export kommt weiter aus
 * `ebur128` und nicht aus einem Versprechen.
 *
 * `TP=-1` hält die Spitze unter −1 dBTP -- dieselbe Grenze, an der `urteil()`
 * misst. `LRA=11` ist der Umfang aus EBU R128.
 *
 * **Offen, mit Zahlen (18. September):** zweimal an diesem Tag kam ein Export
 * mit −0,8 bzw. −0,5 dBTP heraus, also über der Grenze -- einmal hier, einmal
 * im Bau #306. In fünfzehn weiteren Läufen lag die Spitze zwischen −2,7 und
 * −1,4. Gemessen am Rohsignal hält `loudnorm` seine Grenze auf das Zehntel
 * genau; was danach draufkommt, ist das Kodieren. Eine Stufe mehr Luft wäre
 * die naheliegende Behebung -- an keinem Material, das ich bauen konnte, liess
 * sie sich in der Kette der App zeigen, und ungezeigt wird hier nichts
 * geändert. Der Befund steht als 7.110 im Fahrplan.
 */
export function normalisierenFilter(ziel) {
    if (typeof ziel !== 'number' || !Number.isFinite(ziel))
        return [];
    return [`loudnorm=I=${ziel}:TP=-1:LRA=11`];
}
//# sourceMappingURL=lautheit.js.map